// Settings > Account and backup > On this device > "Where your saves live": what this browser keeps,
// which of it a backup carries, and what can erase it. Sizes are worked out when the row is drawn,
// never at boot, and nothing here leaves the device (shared/storage-health.js is a lazy chunk).
import { button } from "../ui.js";
import { loadHealth } from "../../shared/health-gate.js";
import { listTracks } from "../../shared/music-store.js";
import { getMediaInfo, listSlots } from "../../shared/wallpaper-media.js";
import { usage as linuxUsage } from "../../shared/linux-store.js";

const h = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const plural = (n, one, many) => n + " " + (n === 1 ? one : many);

// One row per kind of data. keys: the localStorage keys it is made of (sized here). carry: a carry
// code takes it ("opt": only when chosen), shown once carry codes exist.
export const ROWS = [
	{ what: "Flashcards, notes, to-do, citations, Markdown, calendar", kept: "File, cloud", carry: true, keys: ["revault:flashcards", "revault:notes", "revault:todo", "revault:citations", "revault:markdown", "revault:calendar"] },
	{ what: "Study days, streak, quests", kept: "File, cloud", carry: true, keys: ["revault:days", "revault:totals", "revault:quests", "revault:streak"] },
	{ what: "Settings, looks, favorites", kept: "File, cloud", carry: true, keys: ["revault:settings", "revault:bundles", "revault:prev-look", "revault:favorites", "revault:bookmarks", "revault:home", "revault:tool-recents", "revault:book-recents", "revault:worldclock", "revault:typerace"] },
	{ what: "History and reading spots", kept: "File, cloud", carry: "opt", keys: ["revault:history", "revault:read-pos"] },
	{ what: "Open tabs", kept: "File only", keys: ["revault:session", "revault:session-offer"] },
	{ what: "Picture wallpaper", kept: "File, cloud", keys: ["revault:wallpaper-image"] },
	{ what: "Music you added", kept: "Not in any backup", id: "music" },
	{ what: "GIF and video wallpapers", kept: "Not in any backup", id: "media" },
	{ what: "Linux machines", kept: "Not in any backup", id: "linux" },
	{ what: "Study Helper chats", kept: "Not in any backup, on purpose", id: "helper" },
	{ what: "Desktop mode files", kept: "Not in any backup. Download important files", id: "desktop" },
	{ what: "Logins for other sites", kept: "Never" },
];
export function keptText(r, carryOn) {
	if (!carryOn || !r.carry) return r.kept;
	return r.carry === "opt" ? r.kept + ". Carry code if you choose" : r.kept + ", carry code";
}

// The top line. persisted: true | false | null (the browser has no answer).
export function topLine(persisted) {
	if (persisted === true) return "This browser has agreed to keep Re:Vault's saves when it runs low on space.";
	if (persisted === false) return "This browser may clear Re:Vault's saves if it runs low on space.";
	return "";
}
export function aboutText(host, carryOn) {
	return "Saves belong to this browser and this address (" + host + "). A different Re:Vault address starts empty. " +
		"School Chromebooks that clear at sign-out, guest windows and incognito erase saves no matter what. Use " + (carryOn ? "a carry code, " : "") + "a save file or a cloud backup to keep them.";
}

// The database names this browser has, or null when it can't list them (opening one to look
// would create it).
async function dbNames() {
	if (!window.indexedDB || !indexedDB.databases) return null;
	try { return (await indexedDB.databases()).map((d) => d.name); } catch { return null; }
}

// Conversations in the Helper's own database, without creating it when it isn't there.
async function helperChats() {
	if (!window.indexedDB || !indexedDB.databases) return null;
	const dbs = await indexedDB.databases();
	if (!dbs.some((d) => d.name === "revault-helper")) return 0;
	return new Promise((res) => {
		let req;
		try { req = indexedDB.open("revault-helper"); } catch { return res(null); }
		req.onupgradeneeded = () => { try { req.transaction.abort(); } catch { /* never create it */ } };
		req.onerror = req.onblocked = () => res(null);
		req.onsuccess = () => {
			const db = req.result;
			try {
				if (!db.objectStoreNames.contains("conversations")) { db.close(); return res(0); }
				const c = db.transaction("conversations").objectStore("conversations").count();
				c.onsuccess = () => { db.close(); res(c.result); };
				c.onerror = () => { db.close(); res(null); };
			} catch { try { db.close(); } catch { /* closed */ } res(null); }
		};
	});
}

export function dataHere(opts = {}) {
	const carryOn = !!opts.carry;
	const box = h("div", "rv-datahere");
	box.setAttribute("role", "group");
	box.setAttribute("aria-label", "Where your saves live");
	const top = h("p", "rv-account-note rv-datahere-top");
	top.hidden = true;
	const ask = button("Ask to keep my saves", async () => {
		ask.disabled = true;
		const m = await loadHealth().catch(() => null);
		const ok = m ? await m.askPersist() : null;
		if (ok) { top.textContent = topLine(true); ask.hidden = true; }
		else { top.textContent = topLine(false) + " It didn't agree this time. Your saves are still here."; ask.disabled = false; }
	});
	ask.hidden = true;
	box.append(top, ask, h("p", "rv-account-note", aboutText(location.host, carryOn)));

	const wrap = h("div", "rv-datahere-wrap");
	const table = h("table", "rv-datahere-table");
	const head = h("tr");
	for (const c of ["What", "Saved in", "In a backup?", "Size"]) { const th = h("th", null, c); th.scope = "col"; head.append(th); }
	const thead = h("thead");
	thead.append(head);
	const body = h("tbody");
	const sizeCells = new Map();
	for (const r of ROWS) {
		const tr = h("tr");
		const name = h("th", null, r.what);
		name.scope = "row";
		const cells = [["Saved in", "This browser"], ["In a backup?", keptText(r, carryOn)], ["Size", ""]].map(([l, t]) => { const td = h("td", null, t); td.dataset.l = l; return td; });
		tr.append(name, ...cells);
		sizeCells.set(r, cells[2]);
		body.append(tr);
	}
	table.append(thead, body);
	wrap.append(table);
	const lsLine = h("p", "rv-account-note");
	const fileLine = h("p", "rv-account-note");
	lsLine.hidden = fileLine.hidden = true;
	box.append(wrap, lsLine, fileLine);

	(async () => {
		const m = await loadHealth().catch(() => null);
		const ls = (() => { try { return localStorage; } catch { return null; } })();
		if (m) {
			const st = await m.health();
			const t = topLine(st.persisted);
			if (t) { top.textContent = t; top.hidden = false; ask.hidden = st.persisted !== false; }
			if (ls) {
				for (const r of ROWS) if (r.keys) sizeCells.get(r).textContent = m.fmtSize(m.lsChars(ls, r.keys));
				lsLine.textContent = "Study work and settings: " + m.fmtSize(m.lsChars(ls)) + " of about 5 MB.";
				lsLine.hidden = false;
			}
			const mb = m.roundMB(st.usage);
			if (mb != null) { fileLine.textContent = "Files: " + mb + " MB used."; fileLine.hidden = false; }
		}
		const cell = (id) => sizeCells.get(ROWS.find((r) => r.id === id));
		const kb = (bytes) => (m ? m.fmtSize(bytes) : "");
		cell("music").textContent = plural(listTracks().length, "song", "songs");
		const media = getMediaInfo();
		const mediaBytes = (media ? Number(media.size) || 0 : 0) + listSlots().reduce((s, x) => s + (Number(x && x.size) || 0), 0);
		cell("media").textContent = kb(mediaBytes);
		cell("desktop").textContent = "Not measured";
		try { const n = await helperChats(); cell("helper").textContent = n == null ? "" : plural(n, "chat", "chats"); } catch { /* unknown */ }
		try {
			// linux-store opens (and so creates) revault-linux: only ask it when something is there.
			const names = await dbNames();
			if (names == null) cell("linux").textContent = "Not measured";
			else {
				const u = names.includes("revault-linux") ? await linuxUsage() : { snapshots: 0, images: 0 };
				const disks = names.filter((n) => typeof n === "string" && n.startsWith("cjFS_/revault-cx-")).length;
				cell("linux").textContent = kb(u.snapshots + u.images) + (disks ? " and " + plural(Math.ceil(disks / 2), "CheerpX disk", "CheerpX disks") : "");
			}
		} catch { /* unknown */ }
	})();
	return box;
}
